import { format, isToday, isValid } from 'date-fns';

import { TimeslotOrderGroupModel } from '@merchant-clients/order';

export type TimeslotHeaderGroup = Pick<
  TimeslotOrderGroupModel,
  'timeSlotStart' | 'timeText' | 'timeTextEnd'
>;

/**
 * The upstream omits `timeSlotStart` for some groups, so the day prefix is
 * dropped rather than rendering a broken header when it cannot be resolved.
 */
const resolveDayLabel = (
  timeSlotStart: number | undefined,
  todayLabel: string,
): string | undefined => {
  if (timeSlotStart === undefined || !Number.isFinite(timeSlotStart)) {
    return undefined;
  }

  const slotStart = new Date(timeSlotStart);

  if (!isValid(slotStart)) {
    return undefined;
  }

  return isToday(slotStart) ? todayLabel : format(slotStart, 'dd-MM');
};

/**
 * The upstream `timeText` fields carry a time of day only, so the day a slot
 * belongs to has to be derived from `timeSlotStart`.
 *
 * `todayLabel` is passed in rather than resolved here so the caller supplies a
 * localised string and this stays independent of React context.
 */
export function formatTimeslotHeader(
  group: TimeslotHeaderGroup,
  todayLabel: string,
): string {
  const timeRange = group.timeTextEnd
    ? `${group.timeText} – ${group.timeTextEnd}`
    : group.timeText;
  const dayLabel = resolveDayLabel(group.timeSlotStart, todayLabel);

  return dayLabel ? `${dayLabel} ${timeRange}` : timeRange;
}
